import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight, CheckCircle2, Clock3, Server, Workflow, type LucideIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { api, type AdminOverview, type TranscodeSettings } from "../api";
import { ErrorNotice, LoadingRows, formatTime } from "../components";

export function OverviewPage() {
  const overview = useQuery({ queryKey: ["overview"], queryFn: () => api<AdminOverview>("/admin/v1/overview"), refetchInterval: 10_000 });
  const settings = useQuery({ queryKey: ["transcode-settings"], queryFn: () => api<TranscodeSettings>("/admin/v1/transcode-settings") });
  const error = overview.error ?? settings.error;
  const pending = overview.isPending || settings.isPending;
  const runtime = overview.data;
  return <div className="page">
    <header className="page-heading"><div><p className="eyebrow">CONTROL PLANE / OVERVIEW</p><h1>运行基线</h1><p>关注积压、容量和异常，再进入任务或节点定位。</p></div></header>
    {error && <ErrorNotice error={error} />}
    {pending ? <LoadingRows columns={4} /> : <>
      <section className="metric-strip" aria-label="运行摘要">
        <Metric icon={Workflow} label="排队任务" value={runtime?.queued_jobs ?? 0} hint={`最老等待 ${formatAge(runtime?.oldest_queued_seconds ?? null)}`} to="/jobs" />
        <Metric icon={Clock3} label="执行中" value={runtime?.running_jobs ?? 0} hint="按节点类型查看下方容量" to="/jobs" />
        <Metric icon={Server} label="READY 节点" value={runtime?.ready_nodes ?? 0} hint={`共 ${runtime?.total_nodes ?? 0} 个已注册节点`} to="/nodes/status" />
        <Metric icon={CheckCircle2} label="24h 成功" value={runtime?.succeeded_jobs_24h ?? 0} hint={`${runtime?.failed_jobs_24h ?? 0} 失败 · ${runtime?.callback_dead_letter ?? 0} 回调死信`} to="/jobs" />
      </section>
      <section className="section-block capacity-by-type" aria-label="按节点类型划分的运行容量">
        <div className="section-heading"><div><p className="eyebrow">CAPACITY / NODE TYPE</p><h2>运行槽位</h2></div><Link to="/nodes/status" className="text-link">查看节点 <ArrowUpRight size={15} /></Link></div>
        <div className="capacity-by-type__rows">
          <CapacityRow name="普通降噪" type="normal-v1" queued={runtime?.normal_queued_jobs ?? 0} running={runtime?.normal_running_jobs ?? 0} ready={runtime?.normal_ready_nodes ?? 0} busy={runtime?.normal_busy_slots ?? 0} total={runtime?.normal_total_slots ?? 0} />
          <CapacityRow name="AI 降噪" type="ai-v1" queued={runtime?.ai_queued_jobs ?? 0} running={runtime?.ai_running_jobs ?? 0} ready={runtime?.ai_ready_nodes ?? 0} busy={runtime?.ai_busy_slots ?? 0} total={runtime?.ai_total_slots ?? 0} />
        </div>
      </section>
      <section className="section-block"><div className="section-heading"><div><p className="eyebrow">转码配置</p><h2>当前生效</h2></div><Link to="/transcode-settings" className="text-link">修改设置 <ArrowUpRight size={15} /></Link></div>
        <div className="release-list"><div><div><strong>MP3 输出码率</strong><span>更新于 {formatTime(settings.data?.updated_at ?? null)}</span></div><b>{settings.data?.mp3_bitrate_kbps ?? "—"}<small> kbps CBR</small></b></div><div><div><strong>默认节点类型</strong><span>任务未指定 node_type 时使用</span></div><b>{settings.data?.default_node_type ?? "—"}</b></div></div>
      </section>
    </>}
  </div>;
}

function CapacityRow({ name, type, queued, running, ready, busy, total }: { name: string; type: string; queued: number; running: number; ready: number; busy: number; total: number }) {
  return <div className="capacity-by-type__row">
    <div className="capacity-by-type__identity"><strong>{name}</strong><span>{type}</span></div>
    <div><small>排队</small><strong>{queued}</strong></div>
    <div><small>执行中</small><strong>{running}</strong></div>
    <div><small>READY 节点</small><strong>{ready}</strong></div>
    <div className="capacity-by-type__slots"><small>忙碌 / 总槽位</small><strong>{busy}<em> / {total}</em></strong><span className="capacity-by-type__track"><span style={{ width: `${total ? Math.min(100, busy / total * 100) : 0}%` }} /></span></div>
  </div>;
}

function Metric({ icon: Icon, label, value, hint, to }: { icon: LucideIcon; label: string; value: number; hint: string; to?: string }) {
  const content = <><div className="metric__label"><Icon size={17} />{label}</div><strong>{value}</strong><p>{hint}</p></>;
  return to ? <Link className="metric" to={to}>{content}</Link> : <div className="metric">{content}</div>;
}

function formatAge(seconds: number | null) {
  if (seconds == null) return "—";
  if (seconds >= 3600) return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
  if (seconds >= 60) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  return `${seconds}s`;
}
